import { FunctionComponent, createElement } from 'react';
import { DraftConfiguration, ViewMap } from '@/types';
import { assertNever } from '@/utils/assertNever';
import {
  isDraftBendingTextConfiguration,
  isDraftDesignConfiguration,
  isDraftSpecialTextConfiguration,
  isDraftTextOrPlaceHolderTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';
import { DesignReadOnlyRenderer } from './designRenderer/DesignReadOnlyRenderer';
import { DesignRenderer } from './designRenderer/DesignRenderer';
import { BendingTextReadOnlyRenderer } from './textRenderer/BendingTextReadOnlyRenderer';
import { BendingTextRenderer } from './textRenderer/BendingTextRenderer';
import { SpecialTextReadOnlyRenderer } from './textRenderer/SpecialTextReadOnlyRenderer';
import { SpecialTextRenderer } from './textRenderer/SpecialTextRenderer';
import { TextReadOnlyRenderer } from './textRenderer/TextReadOnlyRenderer';
import { TextRenderer } from './textRenderer/TextRenderer';

export interface RendererProps<Configuration extends DraftConfiguration = DraftConfiguration> {
  configuration: Configuration;
  viewMap: ViewMap;
  readOnly: boolean;
  isSelected: boolean;
  hasErrors: boolean;
  onShowEdit?: () => void;
}

const getRendererForConfiguration = (configuration: DraftConfiguration, readOnly: boolean) => {
  if (isDraftDesignConfiguration(configuration)) {
    return readOnly ? DesignReadOnlyRenderer : DesignRenderer;
  }

  if (isDraftTextOrPlaceHolderTextConfiguration(configuration)) {
    return readOnly ? TextReadOnlyRenderer : TextRenderer;
  }

  if (isDraftBendingTextConfiguration(configuration)) {
    return readOnly ? BendingTextReadOnlyRenderer : BendingTextRenderer;
  }

  if (isDraftSpecialTextConfiguration(configuration)) {
    return readOnly ? SpecialTextReadOnlyRenderer : SpecialTextRenderer;
  }

  return assertNever(configuration);
};

export const ConfigurationRenderer: FunctionComponent<RendererProps> = (props) => {
  const renderer = getRendererForConfiguration(
    props.configuration,
    props.readOnly
  ) as FunctionComponent<RendererProps>;
  return createElement(renderer, props);
};
